<template>
  <div>
    <!-- Hack to cover left sidebar -->
    <v-overlay class="sidebar-cover-overlay" v-if="showDialog">
      <div class="sidebar-cover-clickable"></div>
    </v-overlay>

    <v-dialog v-model="showDialog" persistent width="70%" max-width="1000">
      <v-stepper v-model="stepper" alt-labels class="stepper">
        <v-stepper-header>
          <template v-for="n in stepCount">
            <v-stepper-step
              :key="`step-${n}-header`"
              :step="n"
              :rules="steps[n - 1].rules"
              :editable="true"
              :complete="steps[n - 1].complete"
              color="primary"
            >
              {{ steps[n - 1].title }}
              <small v-if="steps[n - 1].optional" class="mt-1">Optional</small>
            </v-stepper-step>

            <v-divider
              v-if="n <= stepCount"
              :key="`step-${n}-divider`"
            ></v-divider>
          </template>

          <v-stepper-step :step="stepCount + 1" color="primary">
            Finish
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <template v-for="n in stepCount">
            <v-stepper-content :key="`step-${n}-content`" :step="n">
              <v-card class="mb-5" min-height="200px" elevation="0">
                <component
                  :is="steps[n - 1].component"
                  :refresh="refresh"
                ></component>
              </v-card>

              <v-btn
                color="primary"
                :disabled="(!steps[n - 1].complete || isSavingRemoved)"
                class="mr-2"
                @click="handleClickNextStep"
              >
                Continue
              </v-btn>

              <v-btn text @click="handleClickCancel"> Cancel </v-btn>

              <v-btn
                v-show="showRemoveButton"
                :disabled="isSavingRemoved"
                class="pull-right"
                @click="handleClickRemove"
              >
                Remove Notification
                <v-progress-circular
                  v-if="isSavingRemoved"
                  indeterminate
                  size="14"
                  width="2"
                  class="ml-2"
                ></v-progress-circular>
              </v-btn>
            </v-stepper-content>
          </template>

          <v-stepper-content :step="stepCount + 1">
            <v-card class="mt-5 text-center" min-height="200px" elevation="0">
              <p
                v-if="(!isError && !isSuccess && !isErrorRemoved && !isSuccessRemoved)"
              >
                Your notification is ready to be saved.
              </p>
              <p v-if="isError" class="error--text">
                There was an issue trying to save the notification.
              </p>
              <p v-if="isSuccess" class="success--text">
                Your notification was successfully saved.
              </p>
              <p v-if="isErrorRemoved" class="error--text">
                There was an issue trying to remove the notification.
              </p>
              <p v-if="isSuccessRemoved" class="success--text">
                Your notification was successfully removed.
              </p>

              <v-btn
                color="success"
                x-large
                :disabled="(isSaving || !validated || isSavingRemoved)"
                class="mt-3 mb-5"
                @click="handleClickFinished"
              >
                Save Notification
                <v-progress-circular
                  v-if="(isSaving || isSavingRemoved)"
                  indeterminate
                  size="14"
                  width="2"
                  class="ml-2"
                ></v-progress-circular>
              </v-btn>
            </v-card>

            <v-btn text @click="handleClickCancel"> Cancel </v-btn>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-dialog>
  </div>
</template>

<script>
import lodashForOwn from "lodash/forOwn";
import lodashDebounce from "lodash/debounce";

import { mapGetters } from "vuex";
import { MODULE_PREFIX_NOTIFICATIONS } from "@/store";
import { GET_NOTIFICATION_RESET } from "@/store/actions.type";
import {
  updateSavedSearchNotification,
  removeSavedSearchNotification,
} from "@/services/search/savedSearch.service";

export default {
  name: "NotificationsStepper",
  components: {
    NotificationsStepFrequency: () =>
      import("@/components/notifications/NotificationsStepFrequency"),
    NotificationsStepShare: () =>
      import("@/components/notifications/NotificationsStepShare"),
  },
  props: {
    show: {
      type: String,
      default: null,
    },
    notificationExists: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dialog: null,
    stepper: 1,
    isSaving: false,
    isError: false,
    isSuccess: false,
    isSavingRemoved: false,
    isSuccessRemoved: false,
    isErrorRemoved: false,
    refresh: null,
    notification: {
      timeOfDay: null,
    },
  }),
  computed: {
    ...mapGetters({
      steps: MODULE_PREFIX_NOTIFICATIONS + "steps",
    }),
    showDialog: {
      get() {
        if (this.dialog === null) {
          return this.show;
        }
        return this.dialog;
      },
      set(value) {
        this.dialog = value;
      },
    },
    stepCount() {
      let counter = 0;
      if (this.steps) {
        lodashForOwn(this.steps, (value, key) => {
          counter++;
        });
      }
      return counter;
    },
    validated() {
      let valid = false;
      if (this.steps) {
        for (let i = 0; i < this.steps.length; i++) {
          const step = this.steps[i];
          valid = true;
          if (!step.complete) {
            console.warn(`Step ${step.name} is not complete`);
            valid = false;
            break;
          }
        }
      }
      return valid;
    },
    showRemoveButton() {
      return this.notificationExists || this.isSavingRemoved;
    },
  },
  methods: {
    handleClickNextStep() {
      this.stepper++;
    },
    handleClickCancel() {
      this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_NOTIFICATION_RESET
      );
      this.showDialog = false;
      this.stepper = 1;
    },
    async handleClickFinished() {
      this.isSaving = true;
      const { isSuccess, isError } = await updateSavedSearchNotification(
        this.notification
      );
      this.isSuccess = isSuccess;
      this.isError = isError;
      this.isSaving = false;
      if (this.isSuccess) {
        lodashDebounce(() => {
          this.showDialog = false;
        }, 1000)();
      }
    },
    async handleClickRemove() {
      this.isSavingRemoved = true;
      const { isSuccess, isError } = await removeSavedSearchNotification(
        this.notification
      );
      this.stepper = this.stepCount + 1;
      this.isSuccessRemoved = isSuccess;
      this.isErrorRemoved = isError;
      if (this.isSuccessRemoved) {
        lodashDebounce(() => {
          this.showDialog = false;
        }, 1000)();
      } else {
        this.isSavingRemoved = false;
      }
    },
  },
  watch: {
    show: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.stepper = 1;
          this.showDialog = true;
          this.isSuccess = false;
          this.isError = false;
          this.isSaving = false;
          this.isSuccessRemoved = false;
          this.isErrorRemoved = false;
          this.isSavingRemoved = false;
          this.refresh = new Date().toISOString();
        }
      },
    },
  },
  beforeDestroy() {
    this.stepper = 1;
    this.showDialog = false;
    this.isSuccess = false;
    this.isError = false;
    this.isSaving = false;
    this.isSuccessRemoved = false;
    this.isErrorRemoved = false;
    this.isSavingRemoved = false;
  },
};
</script>

<style>
.stepper .v-stepper__label {
  text-align: center !important;
}
</style>

<style scoped>
.sidebar-cover-overlay.v-overlay {
  left: 0px !important;
}
.sidebar-cover-clickable {
  width: 100vw;
  height: 100vh;
}
</style>
